import _ from 'lodash'
import Vue from 'vue'
import { Module, Action, getModule, Mutation, VuexModule } from 'vuex-module-decorators'
import store from '@/store'
import { IFormStore, UpdatePayload } from '@/store/types'
import * as ApiPath from '@/tcw/constant/ApiPath'
import { ApiGeneralSinglePayload } from '@/tcw/api/types'
import * as tcw from '@/tcw/utils/TcwService'
import { TCWUnexpectedError, TCWBadDataStructureError } from '@/plugins/TCWBaseError'
import { MethodType, ApiAgent } from '@/plugins/ApiRequest'
import { KeyInfo } from '@/components/common/form/types'
import { sessionStore } from '@/store/session/SessionStoreModule'
import { DEFAULT_KAISHI } from '@/tcw/constant/TcwConstant'
import { DEFAULT_SHURYO } from '@/tcw/constant/TcwConstant'

/**
 * 検証用ストアデータ
 * 検証データリストはストアに保持せず、編集データだけストアに入れる。
 * 編集データをストアに入れる理由は画面移動しても編集状態を保持しておくため。
 * 編集中に別のデータを編集しようとしたら警告をだす。
 *
 * Store利用者側からはGetterでデータ取得、Actionでデータ更新を行い、Mutationは基本呼ばれないようにする。
 * * fetchまたはcreateして編集データをセット
 * * updateでStoreデータを更新
 * * submitでサーバへ送信
 * * deleteでデータ消去
 * * clearで編集データ吐き
 */
@Module({
  name: 'KintaiPatternFormStore',
  store,
  namespaced: true,
  dynamic: true
})
export class KintaiPatternFormStore extends VuexModule
  implements IFormStore<ApiGeneralSinglePayload> {
  // 編集データ
  protected editTargetData: ApiGeneralSinglePayload | null = null
  // 編集前データ保持用
  protected origin: ApiGeneralSinglePayload | null = null

  /****************
   * Getter
   ****************/
  public get ready(): boolean {
    return this.editTargetData !== null
  }
  get isNewRecord(): boolean {
    const id = this.editTargetData?.id
    return id === null || id === undefined
  }
  public get editData(): ApiGeneralSinglePayload {
    return this.editTargetData ? this.editTargetData : {}
  }
  public get isLock(): boolean {
    return false
  }
  public get apiPath(): { [type: string]: string } {
    return ApiPath.Paths.KintaiPatternEdit
  }
  public get isChanged(): boolean {
    if (this.editTargetData === null) {
      return this.origin !== null
    }
    if (this.origin === null) {
      return this.editTargetData !== null
    }
    const destKeys = Object.keys(this.editTargetData)
    const srcKeys = Object.keys(this.origin)

    if (destKeys.length !== srcKeys.length) {
      return true
    }

    const isChanged = destKeys.some(d => {
      return this.editTargetData && this.origin ? this.editTargetData[d] !== this.origin[d] : false
    })

    return isChanged
  }

  /****************
   * Action
   ****************/

  /**
   * 空データ取得
   */
  @Action({ rawError: true })
  public async create(): Promise<void> {
    await this.fetchEmpty()
  }

  /**
   * 空データ取得
   */
  @Action({ rawError: true })
  public async fetchEmpty(): Promise<ApiGeneralSinglePayload> {
    // API実行
    try {
      const apiAgent = ApiAgent.create(tcw.getApiUrlBase(), sessionStore.sessionToken)
      const response: ApiGeneralSinglePayload = await apiAgent.call<ApiGeneralSinglePayload>(
        this.apiPath.empty,
        'GET'
      )

      // デフォルトの開始終了時刻を設定
      response.kaishi = DEFAULT_KAISHI
      response.shuryo = DEFAULT_SHURYO

      // STOREにセットする
      this.setEditTarget(response)

      // 呼び出し側に送る
      return response
    } catch (e) {
      // console.error("failure fetch form data", e);
      return Promise.reject(e)
    }
  }

  /**
   * 編集データセット
   * @param id 編集データID
   */
  @Action({ rawError: true })
  public async fetch(keyInfo: KeyInfo): Promise<ApiGeneralSinglePayload> {
    // API実行
    try {
      const path = ApiPath.buildPath(this.apiPath.get, keyInfo)
      const apiAgent = ApiAgent.create(tcw.getApiUrlBase(), sessionStore.sessionToken)
      const response: ApiGeneralSinglePayload = await apiAgent.call<ApiGeneralSinglePayload>(
        path,
        'GET'
      )

      // STOREにセットする
      this.setEditTarget(response)

      // 呼び出し側に送る
      return response
    } catch (e) {
      console.error('failure fetch form data', e)
      return Promise.reject(e)
    }
  }

  /**
   * 編集データサーバ送信
   */
  @Action({ rawError: true })
  public async submit(): Promise<ApiGeneralSinglePayload> {
    if (this.ready) {
      // APIパラメータを生成
      const path: string = this.isNewRecord ? this.apiPath.post : this.apiPath.put
      const method: MethodType = this.isNewRecord ? 'POST' : 'PUT'

      // API実行
      const apiAgent = ApiAgent.create(tcw.getApiUrlBase(), sessionStore.sessionToken)
      const response: ApiGeneralSinglePayload = await apiAgent.call<ApiGeneralSinglePayload>(
        path,
        method,
        this.editData as ApiGeneralSinglePayload
      )

      // レスポンス解析
      return response
    } else {
      // データがない場合はエラー
      return Promise.reject(new TCWUnexpectedError('データがセットされていません'))
    }
  }

  /**
   * 編集データサーバ送信
   */
  @Action({ rawError: true })
  public async delete(key: KeyInfo): Promise<boolean> {
    // API実行
    const path = ApiPath.buildPath(this.apiPath.delete, key)
    const apiAgent = ApiAgent.create(tcw.getApiUrlBase(), sessionStore.sessionToken)
    await apiAgent.call(path, 'DELETE')
    return true
  }

  @Action({ rawError: true })
  public updateRecord(payload: UpdatePayload): void {
    this.update(payload)
  }
  @Action({ rawError: true })
  public clear(): void {
    this.setEditTarget(null)
  }

  /****************
   * Mutation
   ****************/
  @Mutation
  public update(payload: UpdatePayload): void {
    if (this.editTargetData === null) {
      throw new TCWBadDataStructureError('データがロードされていません')
    }
    Object.keys(payload).forEach((property: string) => {
      const updateValue = payload[property]
      console.debug(`update record: ${property} => ${updateValue}`)
      if (this.editTargetData) {
        // non-null-assertion回避
        Vue.set(this.editTargetData, property, updateValue)
      }
    })
  }
  @Mutation
  protected setEditTarget(record: ApiGeneralSinglePayload | null): void {
    this.editTargetData = record
    this.origin = _.cloneDeep(record)
  }
}

export const kintaiPatternStore = getModule(KintaiPatternFormStore)
